import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../utils/api";
import MainCard from "components/MainCard";
import PageHeader from "components/PageHeader";
import {
  Avatar, Box, Button, Chip, CircularProgress,
  Divider, Grid, IconButton, Stack, Typography,
} from "@mui/material";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined, CloseCircleOutlined,
  MobileOutlined, MailOutlined,
  EnvironmentOutlined, HistoryOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Dialog, DialogContent, DialogTitle,
  Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow,
  Tooltip,
} from "@mui/material";


function LocationHistoryDialog({ open, onClose, deviceId }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    if (!open || !deviceId) return;
    setLoading(true);
    setError(null);
    setHistory([]);
    api.get(`/mobile/device/location/history/${deviceId}`)
      .then((res) => setHistory(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch((err) => setError(err.response?.data?.message || "Failed to load history"))
      .finally(() => setLoading(false));
  }, [open, deviceId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <HistoryOutlined style={{ color: "#1677ff" }} />
          <Typography variant="h6">Location History</Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Device: {deviceId}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {!loading && error && (
          <Box sx={{ textAlign: "center", p: 4 }}>
            <Typography color="error" variant="body2">{error}</Typography>
          </Box>
        )}
        {!loading && !error && history.length === 0 && (
          <Box sx={{ textAlign: "center", p: 4 }}>
            <EnvironmentOutlined style={{ fontSize: 36, opacity: 0.2 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No location history found
            </Typography>
          </Box>
        )}
        {!loading && !error && history.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.50" }}>
                  <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Latitude</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Longitude</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Map</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((h, idx) => (
                  <TableRow key={h.id} hover>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {idx + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                        {h.latitude ?? "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                        {h.longitude ?? "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {h.latitude && h.longitude ? (
                        <Tooltip title={`${h.latitude}, ${h.longitude}`}>
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() =>
                              window.open(
                                `https://maps.google.com/?q=${h.latitude},${h.longitude}`,
                                "_blank"
                              )
                            }
                          >
                            <EnvironmentOutlined style={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      ) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Info Row ──────────────────────────────────────────────────
function InfoRow({ label, value }) {
  return (
    <Box sx={{
      display: "flex", alignItems: "flex-start",
      py: 1.5, borderBottom: "0.5px solid",
      borderColor: "divider",
      "&:last-child": { borderBottom: "none" },
    }}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ minWidth: 180, flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1 }}>{value}</Box>
    </Box>
  );
}

// ── Main Component ────────────────────────────────────────────
export default function CustomerForm() {
  const navigate  = useNavigate();
  const { id }    = useParams();

  const [customer,  setCustomer]  = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [histOpen,  setHistOpen]  = useState(false);

  // ── GET /customer/:id ─────────────────────────────────────
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    api.get(`/customer/${id}`)
      .then((res) => setCustomer(res.data?.data || null))
      .catch((err) => {
        console.error("[CustomerForm] load error:", err);
        setError(err.response?.data?.message || "Failed to load customer");
      })
      .finally(() => setLoading(false));
  }, [id]);

  // ── helpers ───────────────────────────────────────────────
  // customer_device_maps join → device_id
  const device   = customer?.CustomerDeviceMaps?.[0]
                ?? customer?.CustomerDeviceMap
                ?? null;
  const deviceId = device?.device_id ?? null;

  // config_models join → current latitude / longitude
  const cfg      = device?.ConfigModel ?? device?.config_model ?? null;
  const lat      = cfg?.latitude  ?? null;
  const lng      = cfg?.longitude ?? null;

  const fmtDate  = (val) =>
    val
      ? new Date(val).toLocaleDateString("en-IN", {
          day: "2-digit", month: "short", year: "numeric",
          hour: "2-digit", minute: "2-digit",
        })
      : "—";

  // ── loading ───────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // ── error ─────────────────────────────────────────────────
  if (error || !customer) {
    return (
      <Box sx={{ textAlign: "center", mt: 8 }}>
        <UserOutlined style={{ fontSize: 56, opacity: 0.2 }} />
        <Typography variant="h6" sx={{ mt: 2 }}>{error || "Customer not found"}</Typography>
        <Button sx={{ mt: 2 }} variant="outlined" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Box>
    );
  }

  // ── render ────────────────────────────────────────────────
  return (
    <Box>

      <PageHeader
        variant="nested"
        onBack={() => navigate(-1)}
        backTitle="Back to customers"
        title={customer.name || "Customer Details"}
        subtitle={customer.mobile_number ? `Mobile: ${customer.mobile_number}` : "View customer profile and activity."}
      />

      <Grid container spacing={3}>

        {/* ── Left: main details ── */}
        <Grid item xs={12} md={8}>

          {/* Profile header card */}
          <MainCard sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center" gap={2.5}>
              <Avatar sx={{
                width: 72, height: 72,
                bgcolor: "primary.lighter",
                fontSize: 28, fontWeight: 600,
              }}>
                {(customer.name || customer.mobile_number || "C")[0].toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
                  {customer.name || "—"}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {customer.mobile_number || "—"}
                </Typography>
                <Stack direction="row" gap={1}>
                  {/* DB: is_active */}
                  <Chip
                    label={customer.is_active ? "Active" : "Inactive"}
                    size="small"
                    color={customer.is_active ? "success" : "error"}
                  />
                  {/* DB: is_verified */}
                  <Chip
                    icon={customer.is_verified
                      ? <CheckCircleOutlined />
                      : <CloseCircleOutlined />}
                    label={customer.is_verified ? "Verified" : "Unverified"}
                    size="small"
                    color={customer.is_verified ? "success" : "warning"}
                    variant="outlined"
                  />
                  {/* DB: login_type */}
                  <Chip
                    icon={customer.login_type === "email"
                      ? <MailOutlined />
                      : <MobileOutlined />}
                    label={customer.login_type === "email" ? "Email login" : "Mobile login"}
                    size="small"
                    variant="outlined"
                  />
                </Stack>
              </Box>
            </Stack>
          </MainCard>

          {/* Account info */}
          <MainCard title="Account Information">
            {/* DB: customers.id */}
            <InfoRow
              label="Customer ID"
              value={
                <Typography variant="body2"
                  sx={{ fontFamily: "monospace", fontSize: 12, color: "text.secondary" }}>
                  {customer.id}
                </Typography>
              }
            />
            {/* DB: customers.name */}
            <InfoRow
              label="Full Name"
              value={
                <Typography variant="body2" fontWeight={500}>
                  {customer.name || "—"}
                </Typography>
              }
            />
            {/* DB: customers.mobile_number */}
            <InfoRow
              label="Mobile Number"
              value={
                <Typography variant="body2" fontWeight={500}>
                  {customer.mobile_number || "—"}
                </Typography>
              }
            />
            {/* DB: customers.email */}
            <InfoRow
              label="Email"
              value={
                <Typography variant="body2" fontWeight={500}>
                  {customer.email || "—"}
                </Typography>
              }
            />
            {/* DB: customers.login_type */}
            <InfoRow
              label="Login Type"
              value={
                <Chip
                  icon={customer.login_type === "email"
                    ? <MailOutlined />
                    : <MobileOutlined />}
                  label={customer.login_type === "email" ? "Email" : "Mobile"}
                  size="small"
                  variant="outlined"
                />
              }
            />
            {/* DB: customers.is_verified */}
            <InfoRow
              label="OTP Verified"
              value={
                <Chip
                  icon={customer.is_verified
                    ? <CheckCircleOutlined />
                    : <CloseCircleOutlined />}
                  label={customer.is_verified ? "Verified" : "Unverified"}
                  size="small"
                  color={customer.is_verified ? "success" : "warning"}
                  variant="outlined"
                />
              }
            />
            {/* DB: customers.is_active */}
            <InfoRow
              label="Account Status"
              value={
                <Chip
                  label={customer.is_active ? "Active" : "Inactive"}
                  size="small"
                  color={customer.is_active ? "success" : "error"}
                />
              }
            />
            {/* DB: customers.createdAt */}
            <InfoRow
              label="Registered On"
              value={
                <Typography variant="body2">{fmtDate(customer.createdAt)}</Typography>
              }
            />
          </MainCard>

        </Grid>

        {/* ── Right: device + location ── */}
        <Grid item xs={12} md={4}>

          {/* Device info */}
          <MainCard title="Device Info" sx={{ mb: 3 }}>
            {/* DB: customer_device_maps.device_id */}
            <InfoRow
              label="Device ID"
              value={
                deviceId
                  ? <Typography variant="body2"
                      sx={{ fontFamily: "monospace", fontSize: 11,
                            wordBreak: "break-all", color: "text.secondary" }}>
                      {deviceId}
                    </Typography>
                  : <Typography variant="body2" color="text.disabled">
                      No device linked
                    </Typography>
              }
            />
          </MainCard>

          {/* Current Location */}
          <MainCard title="Current Location" sx={{ mb: 3 }}>
            {/* DB: config_models.latitude + longitude */}
            {lat && lng ? (
              <>
                <InfoRow
                  label="Latitude"
                  value={
                    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                      {lat}
                    </Typography>
                  }
                />
                <InfoRow
                  label="Longitude"
                  value={
                    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                      {lng}
                    </Typography>
                  }
                />
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    size="small"
                    startIcon={<EnvironmentOutlined />}
                    color="success"
                    onClick={() =>
                      window.open(
                        `https://maps.google.com/?q=${lat},${lng}`,
                        "_blank"
                      )
                    }
                  >
                    Open in Google Maps
                  </Button>
                </Box>
              </>
            ) : (
              <Box sx={{ textAlign: "center", py: 2 }}>
                <EnvironmentOutlined
                  style={{ fontSize: 36, opacity: 0.2 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  No location data available
                </Typography>
              </Box>
            )}
          </MainCard>

          {/* Location History */}
          <MainCard title="Location History">
            {deviceId ? (
              <Button
                variant="outlined"
                fullWidth
                startIcon={<HistoryOutlined />}
                onClick={() => setHistOpen(true)}
              >
                View Location History
              </Button>
            ) : (
              <Box sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No device linked — history unavailable
                </Typography>
              </Box>
            )}
          </MainCard>

        </Grid>
      </Grid>

      {/* Location History Dialog
          GET /mobile/device/location/history/:device_id
          Returns: location_histories → id, latitude, longitude */}
      <LocationHistoryDialog
        open={histOpen}
        deviceId={deviceId}
        onClose={() => setHistOpen(false)}
      />

    </Box>
  );
}